/**
 * Force Password Upgrade Modal
 *
 * Rendered when the authenticated user has `needs_password_upgrade` set,
 * which means their stored Django password failed the Cognito policy and
 * the next login via Cognito SRP would fail. Blocks all interaction
 * until the user picks a compliant password.
 *
 * Reuses the session-timeout overlay/modal styles for visual consistency
 * with the only other globally-mounted blocking modal.
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './SessionTimeout.css';
import Button from './ui/Button';
import Input from './ui/Input';

const PASSWORD_RULES = [
  { test: (p: string) => p.length >= 12, label: 'At least 12 characters' },
  { test: (p: string) => /[A-Z]/.test(p), label: 'An uppercase letter' },
  { test: (p: string) => /[a-z]/.test(p), label: 'A lowercase letter' },
  { test: (p: string) => /[0-9]/.test(p), label: 'A number' },
  { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: 'A symbol' },
];

export const ForcePasswordUpgrade: React.FC = () => {
  const { user, isAuthenticated, changePassword, logout } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!isAuthenticated || !user?.needs_password_upgrade) {
    return null;
  }

  const failingRule = PASSWORD_RULES.find((rule) => !rule.test(newPassword));
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit =
    !submitting && oldPassword.length > 0 && !failingRule && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword, confirmPassword);
      // AuthContext clears the flag on success; the modal unmounts itself.
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not update password.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="session-timeout-overlay" role="dialog" aria-modal="true">
      <div
        className="session-timeout-modal"
        style={{ maxWidth: 460, textAlign: 'left' }}
      >
        <h2 className="session-timeout-title" style={{ textAlign: 'center' }}>
          Update your password
        </h2>
        <p className="session-timeout-message">
          We&rsquo;ve strengthened our password requirements. Please choose a
          new password that meets the rules below to continue.
        </p>
        <form onSubmit={handleSubmit} style={{ marginTop: '1rem' }}>
          <label
            htmlFor="upgrade-old-password"
            style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}
          >
            Current password
          </label>
          <Input controlSize="sm"
            id="upgrade-old-password"
            type="password"
            autoComplete="current-password"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)} style={{ marginBottom: '0.75rem' }}
            disabled={submitting}
            required
          />
          <label
            htmlFor="upgrade-new-password"
            style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}
          >
            New password
          </label>
          <Input controlSize="sm"
            id="upgrade-new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)} style={{ marginBottom: '0.75rem' }}
            disabled={submitting}
            required
          />
          <label
            htmlFor="upgrade-confirm-password"
            style={{ display: 'block', fontSize: '0.85rem', marginBottom: 4 }}
          >
            Confirm new password
          </label>
          <Input controlSize="sm"
            id="upgrade-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)} style={{ marginBottom: '0.75rem' }}
            disabled={submitting}
            required
          />
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: '0 0 1rem',
              fontSize: '0.85rem',
            }}
          >
            {PASSWORD_RULES.map((rule) => {
              const ok = rule.test(newPassword);
              return (
                <li
                  key={rule.label}
                  style={{
                    color: ok ? 'var(--feedback-success)' : 'var(--text-secondary)',
                  }}
                >
                  {ok ? '✓' : '•'} {rule.label}
                </li>
              );
            })}
            <li
              style={{
                color: passwordsMatch
                  ? 'var(--feedback-success)'
                  : 'var(--text-secondary)',
              }}
            >
              {passwordsMatch ? '✓' : '•'} Passwords match
            </li>
          </ul>
          {error && (
            <p
              role="alert"
              style={{
                color: 'var(--feedback-error)',
                fontSize: '0.85rem',
                marginBottom: '0.75rem',
              }}
            >
              {error}
            </p>
          )}
          <div className="session-timeout-actions">
            <Button
              type="submit"
              variant="primary" block
              disabled={!canSubmit}
            >
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
            <Button
              variant="ghost" block
              onClick={logout}
              disabled={submitting}
            >
              Log out
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForcePasswordUpgrade;
